import { DaySequenceStatus } from '@core/generated/gql-types';
import {
  hasAnyPermission,
  hasPermission,
  Permission,
} from '@core/permission/permission.constants';
import type {
  PortalAccessContext,
  PortalAudience,
} from '@/access/portalAccess.types';
import { FeatureFlag, isFeatureEnabled } from '@/utils/featureFlags';
import type { PayrollViewInput } from '@/modules/payroll/payroll.viewInput';

export type PayrollTabKey = 'payRunReadiness' | 'payroll';

/** Sub-sections rendered inside the Payroll tab. */
export type PayrollSectionKey = 'daySequences' | 'payRuns';

type VisibilityRule = {
  /** Hide until the flag is enabled for the current environment. */
  featureFlag?: FeatureFlag;
  /** Portal audiences that can see it. */
  audiences: PortalAudience[];
  /** Restrict to portal admins (TenantAdmin + HqAdmin). */
  adminOnly?: boolean;
  /** Backend permissions (any of) required. */
  requiredPermissions?: Permission[];
};

const ANY_PAYROLL_PERMISSION: Permission[] = [
  Permission.PayrollRunPayrollReadynessReport,
  Permission.PayrollRunPaycycle,
  Permission.PayrollForceSyncExistingPaycycle,
];

type PayrollTabDefinition<TKey extends string> = VisibilityRule & {
  key: TKey;
  label: string;
};

/**
 * Payroll tabs in display order. Each tab is gated by, in order:
 * feature flag -> audience -> admin role -> backend permissions.
 */
const PAYROLL_TABS: PayrollTabDefinition<PayrollTabKey>[] = [
  {
    key: 'payRunReadiness',
    label: 'Pay Run Readiness',
    audiences: ['tenant'],
    requiredPermissions: [Permission.PayrollRunPayrollReadynessReport],
  },
  {
    key: 'payroll',
    label: 'Payroll',
    audiences: ['tenant'],
    requiredPermissions: ANY_PAYROLL_PERMISSION,
  },
];

const PAYROLL_SECTIONS: PayrollTabDefinition<PayrollSectionKey>[] = [
  {
    key: 'daySequences',
    label: 'Day Sequences',
    audiences: ['tenant'],
    requiredPermissions: ANY_PAYROLL_PERMISSION,
  },
  {
    key: 'payRuns',
    label: 'Pay Runs & Cycle Logs',
    audiences: ['tenant'],
    requiredPermissions: ANY_PAYROLL_PERMISSION,
  },
];

export type PayrollView = {
  audience: PortalAudience;
  tabs: PayrollTabKey[];
  defaultTab: PayrollTabKey | undefined;
  payrollSections: PayrollSectionKey[];
  actions: {
    /** Statuses the user may move a day sequence to (approve / escalate). */
    daySequenceStatusOptions: DaySequenceStatus[];
    /** Queue a pay run via `initiatePayRunForTenant`. */
    showRunPayCycle: boolean;
    /** Re-push a cycle log to the payroll integration. */
    showForceSyncCycle: boolean;
    showExportReadiness: boolean;
  };
};

const isVisible = (
  rule: VisibilityRule,
  access: PortalAccessContext,
): boolean => {
  if (rule.featureFlag && !isFeatureEnabled(rule.featureFlag)) {
    return false;
  }

  if (!rule.audiences.includes(access.audience)) {
    return false;
  }

  if (rule.adminOnly && !access.isPortalAdmin) {
    return false;
  }

  if (
    rule.requiredPermissions?.length &&
    !hasAnyPermission(access.permissions, rule.requiredPermissions)
  ) {
    return false;
  }

  return true;
};

const toLabelledTabs = <TKey extends string>(
  definitions: PayrollTabDefinition<TKey>[],
  keys: TKey[],
): { key: TKey; label: string }[] =>
  definitions
    .filter((definition) => keys.includes(definition.key))
    .map((definition) => ({ key: definition.key, label: definition.label }));

export const getPayrollTabs = (tabKeys: PayrollTabKey[]) =>
  toLabelledTabs(PAYROLL_TABS, tabKeys);

export const getPayrollSections = (sectionKeys: PayrollSectionKey[]) =>
  toLabelledTabs(PAYROLL_SECTIONS, sectionKeys);

export const getPayrollView = ({ access }: PayrollViewInput): PayrollView => {
  const { audience } = access;

  const tabs = PAYROLL_TABS.filter((tab) => isVisible(tab, access)).map(
    (tab) => tab.key,
  );
  const payrollSections = PAYROLL_SECTIONS.filter((section) =>
    isVisible(section, access),
  ).map((section) => section.key);

  const can = (permission: Permission) =>
    hasPermission(access.permissions, permission);

  const daySequenceStatusOptions: DaySequenceStatus[] = [
    ...(can(Permission.ApproveDaySequences)
      ? [DaySequenceStatus.ReadyForPay]
      : []),
    ...(can(Permission.DisputeDaySequences)
      ? [DaySequenceStatus.EscalatedToTenant]
      : []),
  ];

  return {
    audience,
    tabs,
    defaultTab: tabs[0],
    payrollSections,
    actions: {
      daySequenceStatusOptions,
      showRunPayCycle: can(Permission.PayrollRunPaycycle),
      showForceSyncCycle: can(Permission.PayrollForceSyncExistingPaycycle),
      showExportReadiness: true,
    },
  };
};
